import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBell,
  FaChevronDown,
  FaEnvelope,
  FaFacebookF,
  FaHome,
  FaInstagram,
  FaMoon,
  FaPhone,
  FaRegEnvelope,
  FaSignInAlt,
  FaSignOutAlt,
  FaSun,
  FaUser,
  FaUserPlus,
  FaUserShield,
} from "react-icons/fa";
import { MdOutlineMessage } from "react-icons/md";
import { IoGrid } from "react-icons/io5";

import { CLUB_SECTIONS, FALLBACK_AVATAR, FALLBACK_LOGO } from "./navbarData";

import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";
import { toAbsolutePhotoUrl } from "../../../services/settingsPhotoServices";

const MENU_ANIMATION_STYLE = `
@keyframes macDrawerIn {
  from {
    opacity: 0;
    transform: translateX(18px) scale(.98);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes macFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes macItemIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes macGlow {
  0%, 100% {
    box-shadow: 0 16px 40px rgba(79, 70, 229, .24);
  }
  50% {
    box-shadow: 0 18px 48px rgba(236, 72, 153, .26);
  }
}

.mac-mobile-backdrop {
  animation: macFadeIn .18s ease-out both;
}

.mac-mobile-panel {
  animation: macDrawerIn .24s cubic-bezier(.22, 1, .36, 1) both;
}

.mac-menu-item {
  animation: macItemIn .28s cubic-bezier(.22, 1, .36, 1) both;
}

.mac-application-glow {
  animation: macGlow 2.8s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .mac-mobile-backdrop,
  .mac-mobile-panel,
  .mac-menu-item,
  .mac-application-glow {
    animation: none !important;
  }
}
`;

const normalizeCount = (count) => {
  const value = Number(count || 0);
  if (value <= 0) return null;
  return value > 99 ? "99+" : value;
};

function CounterBubble({ count, compact = false }) {
  const value = normalizeCount(count);

  if (!value) return null;

  return (
    <span
      className={[
        "absolute flex items-center justify-center rounded-full bg-red-500 font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-950",
        compact
          ? "-right-1 -top-1 min-h-5 min-w-5 px-1 text-[10px]"
          : "right-3 top-1/2 min-h-6 min-w-6 -translate-y-1/2 px-1.5 text-xs",
      ].join(" ")}
    >
      {value}
    </span>
  );
}

const getUserInitials = (user) => {
  const firstName = user?.prenom || user?.first_name || "";
  const lastName = user?.nom || user?.last_name || "";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();

  if (initials) return initials.toUpperCase();
  if (user?.email) return user.email.charAt(0).toUpperCase();

  return "U";
};

function UserAvatar({ user, size = "lg" }) {
  const initials = useMemo(() => getUserInitials(user), [user]);

  const rawPhoto =
    user?.photoURL ||
    user?.photo_url ||
    user?.photo ||
    user?.avatar ||
    FALLBACK_AVATAR;

  const photoUrl = toAbsoluteUserPhotoUrl(rawPhoto);

  const sizeClass =
    size === "xl" ? "h-20 w-20" : size === "lg" ? "h-16 w-16" : "h-10 w-10";

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt="Utilisateur"
        className={`${sizeClass} shrink-0 rounded-3xl object-cover ring-4 ring-white/70 shadow-lg dark:ring-slate-800`}
        onError={(event) => {
          event.currentTarget.src = FALLBACK_AVATAR;
        }}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-lg font-bold text-white shadow-lg ring-4 ring-white/70 dark:ring-slate-800`}
    >
      {initials}
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  to,
  onClick,
  count,
  tone = "slate",
}) {
  const toneClass = {
    slate:
      "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800",
    sky: "bg-sky-50 text-sky-700 ring-sky-200 hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
    violet:
      "bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    amber:
      "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
  };

  const content = (
    <>
      <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/70 shadow-sm dark:bg-white/10">
        <Icon className="text-xl" />
        <CounterBubble count={count} compact />
      </span>
      <span className="mt-2 text-[14px] font-semibold leading-tight">
        {label}
      </span>
    </>
  );

  const className = [
    "relative flex min-h-[84px] flex-col items-center justify-center rounded-3xl p-2 text-center text-xs font-bold ring-1 transition active:scale-[.98]",
    toneClass[tone] || toneClass.slate,
  ].join(" ");

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

function MobileMenuItem({
  icon: Icon,
  children,
  onClick,
  to,
  count,
  delay = 0,
  danger = false,
  badge,
}) {
  const className = [
    "mac-menu-item relative flex w-full items-center gap-3 rounded-3xl px-4 py-3.5 text-left text-sm font-bold transition active:scale-[.99]",
    danger
      ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
      : "text-slate-700 hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400",
  ].join(" ");

  const content = (
    <>
      {Icon && (
        <span
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",
            danger
              ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
          ].join(" ")}
        >
          <Icon />
        </span>
      )}

      <span className="min-w-0 flex-1 truncate">{children}</span>

      {badge && (
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          {badge}
        </span>
      )}

      <CounterBubble count={count} />
    </>
  );

  const style = {
    animationDelay: `${delay}ms`,
  };

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className} style={style}>
      {content}
    </button>
  );
}

function SectionToggle({
  icon: Icon,
  title,
  open,
  onToggle,
  children,
  delay = 0,
  badge,
}) {
  return (
    <div className="mac-menu-item" style={{ animationDelay: `${delay}ms` }}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-3xl px-4 py-3.5 text-left text-sm font-bold text-slate-700 transition hover:bg-slate-100 hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-orange-400"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {Icon ? <Icon /> : "P"}
        </span>

        <span className="min-w-0 flex-1 truncate">{title}</span>

        {badge && (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
            {badge}
          </span>
        )}

        <FaChevronDown
          className={`text-xs transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={[
          "grid transition-all duration-300 ease-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="overflow-hidden">
          <div className="ml-5 mt-1 border-l border-slate-200 pl-3 dark:border-slate-800">
            <div className="flex flex-col gap-1 py-2">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-12 w-12 bg-slate-100 items-center justify-center rounded-full border border-slate-200 text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-white"
    >
      <Icon className="text-xl" />
    </button>
  );
}

export default function NavbarMobileMenu({
  settings,
  logoSrc,
  pagesActives,
  user,
  isAuthenticated,
  isAdmin,
  IconComponent,
  theme,
  unreadCount,
  unreadMessageCount,
  unreadCountContactForm,
  onThemeToggle,
  onScrollToSection,
  onInscription,
  onApplication,
  onLogout,
  onClose,
}) {
  const [clubOpen, setClubOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);

  const resolvedLogo =
    logoSrc || toAbsolutePhotoUrl(settings?.logo_club || "") || FALLBACK_LOGO;

  const userFullName =
    `${user?.nom || ""} ${user?.prenom || ""}`.trim() ||
    `${user?.prenom || ""} ${user?.nom || ""}`.trim() ||
    user?.email ||
    "Utilisateur";

  const roleLabel = isAdmin ? "Administrateur" : "Utilisateur";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const closeAndScroll = (targetId) => {
    onScrollToSection(targetId);
  };

  return (
    <div
      className="fixed inset-0 z-[90] lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Menu mobile"
    >
      <style>{MENU_ANIMATION_STYLE}</style>

      <div
        className="mac-mobile-backdrop absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside
        className="mac-mobile-panel absolute right-0 top-0 flex h-[100dvh] w-full max-w-[480px] flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="shrink-0 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/"
              onClick={() => closeAndScroll("accueil")}
              className="flex min-w-0 items-center gap-3"
            >
              <img
                src={resolvedLogo}
                alt={`Logo ${settings?.nom_club || "du club"}`}
                className="h-14 w-14 shrink-0 rounded-2xl object-contain"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK_LOGO;
                }}
              />

              <div className="min-w-0">
                <p className="truncate text-lg font-bold text-slate-950 dark:text-white">
                  {settings?.nom_club || "MonAppliClub"}
                </p>

                <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Menu mobile
                </p>
              </div>
            </Link>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800"
              aria-label="Fermer le menu"
              title="Fermer"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {isAuthenticated ? (
            <div
              className="mac-menu-item overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-4 text-white shadow-xl dark:border-slate-800"
              style={{ animationDelay: "20ms" }}
            >
              <div className="pointer-events-none absolute" />

              <div className="flex items-center gap-4">
                <UserAvatar user={user} size="xl" />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-bold">{userFullName}</p>

                  {user?.email && (
                    <p className="mt-0.5 truncate text-xs text-slate-300">
                      {user.email}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white ring-1 ring-white/15">
                      {roleLabel}
                    </span>

                    {isAdmin && (
                      <span className="rounded-full bg-orange-400/20 px-3 py-1 text-xs font-bold text-orange-100 ring-1 ring-orange-300/20">
                        Accès admin
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Link
                  to="/profile"
                  onClick={onClose}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white/10 px-3 text-sm font-bold text-white ring-1 ring-white/15 transition hover:bg-white/15"
                >
                  <FaUser />
                  Mon profil
                </Link>

                <button
                  type="button"
                  onClick={onThemeToggle}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white/10 px-3 text-sm font-bold text-white ring-1 ring-white/15 transition hover:bg-white/15"
                >
                  {theme === "dark" ? <FaSun /> : <FaMoon />}
                  {theme === "dark" ? "Mode clair" : "Mode sombre"}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="mac-menu-item rounded-[2rem] border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              style={{ animationDelay: "20ms" }}
            >
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Bienvenue
              </p>

              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Connectez-vous pour accéder à votre espace club.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Link
                  to="/login"
                  onClick={onClose}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950"
                >
                  <FaSignInAlt />
                  Connexion
                </Link>

                <Link
                  to="/register"
                  onClick={onClose}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white px-3 text-sm font-bold text-slate-800 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700"
                >
                  <FaUserPlus />
                  Compte
                </Link>
              </div>
            </div>
          )}

          {isAuthenticated && (
            <>
              <button
                type="button"
                onClick={onApplication}
                className="mac-menu-item mac-application-glow mt-4 flex w-full items-center gap-4 overflow-hidden rounded-[2rem] bg-gradient-to-r from-[#013E8E] via-indigo-600 to-fuchsia-600 p-4 text-left text-white shadow-xl transition active:scale-[.99]"
                style={{ animationDelay: "70ms" }}
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl bg-white/15 text-3xl ring-1 ring-white/20">
                  <IoGrid />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-lg font-bold">
                    Ouvrir l’application
                  </span>

                  <span className="mt-0.5 block text-xs font-semibold text-white/80">
                    Accédez à votre espace adhérent, messages, calendrier,
                    boutique et outils club.
                  </span>
                </span>

                {IconComponent && (
                  <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-xl sm:flex">
                    <IconComponent />
                  </span>
                )}
              </button>

              <div
                className="mac-menu-item mt-4 grid grid-cols-3 gap-2"
                style={{ animationDelay: "110ms" }}
              >
                <QuickAction
                  icon={FaRegEnvelope}
                  label="Messages"
                  to="/messages"
                  onClick={onClose}
                  count={unreadMessageCount}
                  tone="sky"
                />

                <QuickAction
                  icon={FaBell}
                  label="Notifications"
                  to="/tableau-de-bord"
                  onClick={onClose}
                  count={unreadCount}
                  tone="violet"
                />

                {isAdmin ? (
                  <QuickAction
                    icon={MdOutlineMessage}
                    label="Formulaire"
                    to="/tableau-de-bord"
                    onClick={onClose}
                    count={unreadCountContactForm}
                    tone="amber"
                  />
                ) : (
                  <QuickAction
                    icon={FaUser}
                    label="Profil"
                    to="/profile"
                    onClick={onClose}
                    tone="slate"
                  />
                )}
              </div>
            </>
          )}

          <div className="mt-4 space-y-1">
            <MobileMenuItem
              icon={FaHome}
              onClick={() => closeAndScroll("accueil")}
              delay={150}
            >
              Accueil
            </MobileMenuItem>

            <SectionToggle
              icon={FaHome}
              title="Club"
              open={clubOpen}
              onToggle={() => setClubOpen((previous) => !previous)}
              delay={180}
              badge={`${CLUB_SECTIONS.length}`}
            >
              {CLUB_SECTIONS.map((item, index) => (
                <MobileMenuItem
                  key={item.targetId}
                  icon={item.icon}
                  onClick={() => closeAndScroll(item.targetId)}
                  delay={index * 20}
                >
                  {item.label}
                </MobileMenuItem>
              ))}
            </SectionToggle>

            {pagesActives.length > 0 && (
              <SectionToggle
                title="Pages personnalisées"
                open={pagesOpen}
                onToggle={() => setPagesOpen((previous) => !previous)}
                delay={210}
                badge={`${pagesActives.length}`}
              >
                {pagesActives.map((page, index) => (
                  <MobileMenuItem
                    key={page.id}
                    to={`/page/${page.slug}`}
                    onClick={onClose}
                    delay={index * 20}
                    badge={String(page.titre || "P").charAt(0)}
                  >
                    {page.titre}
                  </MobileMenuItem>
                ))}
              </SectionToggle>
            )}

            <div
              className="mac-menu-item my-3 border-t border-slate-200 dark:border-slate-800"
              style={{ animationDelay: "230ms" }}
            />

            <MobileMenuItem
              icon={FaSignInAlt}
              onClick={onInscription}
              delay={250}
            >
              Inscription
            </MobileMenuItem>

            {!isAuthenticated && (
              <>
                <MobileMenuItem
                  icon={theme === "dark" ? FaSun : FaMoon}
                  onClick={onThemeToggle}
                  delay={270}
                >
                  {theme === "dark" ? "Mode clair" : "Mode sombre"}
                </MobileMenuItem>

                <MobileMenuItem
                  icon={FaSignInAlt}
                  to="/login"
                  onClick={onClose}
                  delay={290}
                >
                  Connexion
                </MobileMenuItem>
              </>
            )}

            {isAuthenticated && isAdmin && (
              <MobileMenuItem
                icon={FaUserShield}
                to="/tableau-de-bord"
                onClick={onClose}
                delay={290}
              >
                Tableau de bord
              </MobileMenuItem>
            )}

            {isAuthenticated && (
              <MobileMenuItem
                icon={FaSignOutAlt}
                onClick={onLogout}
                delay={320}
                danger
              >
                Déconnexion
              </MobileMenuItem>
            )}
          </div>

          {(settings.mail_club ||
            settings.tel_club ||
            settings.facebook_club ||
            settings.instagram_club) && (
            <div
              className="mac-menu-item mt-5 border-t border-slate-200 pt-4 dark:border-slate-800"
              style={{ animationDelay: "350ms" }}
            >
              <p className="mb-3 px-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                Contact
              </p>

              <div className="flex flex-wrap gap-3 px-2">
                {settings.mail_club && (
                  <ContactButton
                    icon={FaEnvelope}
                    label="E-mail"
                    onClick={() => {
                      window.location.href = `mailto:${settings.mail_club}`;
                    }}
                  />
                )}

                {settings.tel_club && (
                  <ContactButton
                    icon={FaPhone}
                    label="Téléphone"
                    onClick={() => {
                      window.location.href = `tel:${settings.tel_club}`;
                    }}
                  />
                )}

                {settings.facebook_club && (
                  <ContactButton
                    icon={FaFacebookF}
                    label="Facebook"
                    onClick={() =>
                      window.open(
                        settings.facebook_club,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  />
                )}

                {settings.instagram_club && (
                  <ContactButton
                    icon={FaInstagram}
                    label="Instagram"
                    onClick={() =>
                      window.open(
                        settings.instagram_club,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
